import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, BACKEND_URL } from '../lib/api';
import { site } from '../data/mock';

const DEFAULT = {
  id: 'evt-2026-11-08-1000', name: 'Coffee & Conversation', name_es: 'Café y Conversación', date_iso: '2026-11-08', time_24: '10:00',
  venue_name: 'Schuster Student Success Center', venue_short: 'Schuster SSC', room: '130',
  campus: 'Columbus State University', address: '4107 University Ave, Columbus, GA 31907',
  maps_url: 'https://www.google.com/maps/search/?api=1&query=Schuster+Student+Success+Center+Columbus+State+University+4107+University+Ave',
  start_utc: '20261108T150000Z', end_utc: '20261108T170000Z', flyer_url: null,
};

const EN_D = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const EN_M = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ES_D = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const ES_M = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const short = (s) => s.slice(0, 3);

export function eventVars(ev, lang) {
  const [y, m, d] = ev.date_iso.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const [hh, mm] = ev.time_24.split(':').map(Number);
  const time = `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
  const name = (lang === 'es' && ev.name_es) || ev.name;
  const roomLabel = ev.room ? `${lang === 'es' ? 'Sala' : 'Room'} ${ev.room}` : '';
  const place = [ev.venue_name, roomLabel, ev.campus].filter(Boolean).join(', ');
  const common = { name, place, time, venue: ev.venue_name, venueShort: ev.venue_short || ev.venue_name, campus: ev.campus, address: ev.address, year: y };
  if (lang === 'es') {
    const md = `${d} de ${ES_M[m - 1]}`;
    return { ...common, room: roomLabel, dayShort: `${cap(short(ES_D[wd]))}, ${d} ${short(ES_M[m - 1])}`, wdMd: `${d} de ${short(ES_M[m - 1])}.`, mdLong: md, dateLong: `${cap(ES_D[wd])}, ${md}`, dateLongLower: `${ES_D[wd]} ${md}`, dateFull: `${cap(ES_D[wd])}, ${md} de ${y}` };
  }
  const md = `${EN_M[m - 1]} ${d}`;
  return { ...common, room: roomLabel, dayShort: `${short(EN_D[wd])}, ${short(EN_M[m - 1])} ${d}`, wdMd: `${EN_D[wd]}, ${short(EN_M[m - 1])} ${d}`, mdLong: md, dateLong: `${EN_D[wd]}, ${md}`, dateLongLower: `${EN_D[wd]}, ${md}`, dateFull: `${EN_D[wd]}, ${md}, ${y}` };
}

export function calendarEvent(ev, lang) {
  const v = eventVars(ev, lang);
  return {
    id: ev.id, label: v.name, dateLong: v.dateFull, time: v.time,
    venue: v.place,
    address: ev.address, mapsUrl: ev.maps_url, startUtc: ev.start_utc, endUtc: ev.end_utc,
  };
}

const EventContext = createContext({ event: DEFAULT, setEvent: () => {}, flyerUrl: site.flyerImage, reload: () => {} });

export function EventProvider({ children }) {
  const [event, setEvent] = useState(DEFAULT);
  const reload = useCallback(() => api.get('/event').then((r) => setEvent(r.data)).catch((err) => console.error('Failed to load event', err)), []);
  useEffect(() => { reload(); }, [reload]);
  const value = useMemo(() => ({
    event, setEvent, reload,
    flyerUrl: event.flyer_url ? `${BACKEND_URL}${event.flyer_url}` : site.flyerImage,
  }), [event, reload]);
  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export const useEvent = () => useContext(EventContext);
